import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useTheme } from '../ui/theme-provider';
import { cn } from '../../lib/utils';
import { Building2, MapPin, Clock, Users, Briefcase, Calendar } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar as CalendarComponent } from '../ui/calendar';
import { format } from 'date-fns';

const JobPostingForm = ({ 
  verifiedPractices, 
  onSubmit, 
  onCancel, 
  loading = false, 
  initialData = null,
  selectedPracticeId = null
}) => {
  const { theme } = useTheme();

  const [formData, setFormData] = useState({
    title: '',
    employmentType: '',
    description: '',
    requirements: '',
    salary: '',
    applicationInstructions: '',
    contactEmail: '',
    selectedPracticeId: selectedPracticeId || '',
    location: '', // Add location field
    expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
  });

  const [error, setError] = useState({}); // FIXED: Initialize as object instead of string

  const employmentTypes = [
    'Full-time',
    'Part-time', 
    'NHS',
    'Contract'
  ];

  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title || '',
        employmentType: initialData.employmentType || '',
        description: initialData.description || '',
        requirements: initialData.requirements || '',
        salary: initialData.salary || '',
        applicationInstructions: initialData.applicationInstructions || '',
        contactEmail: initialData.contactEmail || '',
        selectedPracticeId: initialData.managedPracticeId || selectedPracticeId || '',
        location: initialData.location || '', // Include location from existing data
        expiresAt: initialData.expiresAt ? 
          (initialData.expiresAt.toDate ? initialData.expiresAt.toDate() : new Date(initialData.expiresAt)) : 
          new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
      });
    } else if (selectedPracticeId) {
      // Auto-populate location when practice is selected
      const practice = verifiedPractices.find(p => p.id === selectedPracticeId);
      setFormData(prev => ({
        ...prev,
        selectedPracticeId,
        location: practice ? `${practice.practiceAddress}` : '',
        expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
      }));
    }
  }, [initialData, selectedPracticeId, verifiedPractices]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (error[field]) {
      setError(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.selectedPracticeId) {
      newErrors.selectedPracticeId = 'Please select a practice';
    }
    if (!formData.title.trim()) {
      newErrors.title = 'Job title is required';
    }
    if (!formData.employmentType) {
      newErrors.employmentType = 'Employment type is required';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Job description is required';
    }
    if (!formData.applicationInstructions.trim()) {
      newErrors.applicationInstructions = 'Application instructions are required';
    }
    if (!formData.contactEmail.trim()) {
      newErrors.contactEmail = 'Contact email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
      newErrors.contactEmail = 'Please enter a valid email address';
    }

    setError(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError({}); // FIXED: Clear as object
    
    if (!validateForm()) {
      return;
    }

    // Get selected practice for location data
    const selectedPracticeData = verifiedPractices.find(p => p.id === formData.selectedPracticeId);

    const jobData = {
      title: formData.title,
      employmentType: formData.employmentType,
      description: formData.description,
      requirements: formData.requirements,
      salary: formData.salary,
      applicationInstructions: formData.applicationInstructions,
      contactEmail: formData.contactEmail,
      managedPracticeId: formData.selectedPracticeId,
      // Include location information for job cards
      location: formData.location || selectedPracticeData?.practiceAddress || '',
      postcode: selectedPracticeData?.practicePostcode || '',
      expiresAt: formData.expiresAt,
      status: 'active'
    };

    onSubmit(jobData);
  };

  const selectedPractice = verifiedPractices.find(p => p.id === formData.selectedPracticeId);

  // Define the consistent style strings
  const lightModeInputClasses = 'bg-white hover:bg-gray-50 focus:bg-white border-gray-300 text-gray-900';
  const darkModeInputClasses = 'bg-[#1F1F2E] hover:bg-[#1F1F2E]/80 border-gray-700 text-white';

  return (
    <Card className={cn(
      "max-w-4xl mx-auto",
      theme === 'light' ? 'bg-white border-gray-200' : 'bg-[#2A2A3E] border-purple-500/20'
    )}>
      <CardHeader>
        <CardTitle className={cn(
          "text-2xl",
          theme === 'light' ? 'text-gray-900' : 'text-white'
        )}>
          {initialData ? 'Edit Job Posting' : 'Create New Job Posting'}
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* FIXED: Only show error banner if we have validation errors */}
          {Object.keys(error).length > 0 && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
              Please fix the errors below to continue.
            </div>
          )}

          {/* Practice Selection */}
          <div className="space-y-2">
            <Label className={cn(
              "text-sm font-medium",
              theme === 'light' ? 'text-gray-700' : 'text-gray-200'
            )}>
              <Building2 className="h-4 w-4 inline mr-1" />
              Select Practice <span className="text-red-500">*</span>
            </Label>
            <Select 
              value={formData.selectedPracticeId} 
              onValueChange={(value) => handleInputChange('selectedPracticeId', value)}
            >
              <SelectTrigger className={cn(
                error.selectedPracticeId ? 'border-red-500' : '',
                theme === 'light' 
                  ? 'bg-white border-gray-300 text-gray-900 placeholder:text-gray-500' 
                  : 'bg-[#1F1F2E] border-gray-700 text-white placeholder:text-gray-400'
              )}>
                <SelectValue placeholder="Choose a verified practice" className={cn(
                  theme === 'light' ? 'text-gray-500' : 'text-gray-400'
                )} />
              </SelectTrigger>
              <SelectContent className={theme === 'light' ? 'bg-white text-gray-900' : ''}>
                {verifiedPractices.map((practice) => (
                  <SelectItem key={practice.id} value={practice.id}>
                    <div className="flex flex-col">
                      <span className={cn("font-medium", theme === 'light' ? 'text-gray-900' : '')}>
                        {practice.practiceName}
                      </span>
                      <span className={cn("text-xs", theme === 'light' ? 'text-gray-500' : 'text-gray-400')}>
                        {practice.practiceAddress} • {practice.practicePostcode}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* FIXED: Check for error properly */}
            {error.selectedPracticeId && (
              <p className="text-red-500 text-xs">{error.selectedPracticeId}</p>
            )}
            {verifiedPractices.length === 0 && (
              <p className={cn(
                "text-xs",
                theme === 'light' ? 'text-amber-600' : 'text-amber-400'
              )}>
                No verified practices available. Please verify a practice first.
              </p>
            )}
          </div>

          {/* Selected Practice Info */}
          {selectedPractice && (
            <div className={cn(
              "p-3 rounded-md border",
              theme === 'light' ? 'bg-blue-50 border-blue-200 text-blue-800' : 'bg-blue-900/20 border-blue-800 text-blue-300'
            )}>
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="h-4 w-4" />
                <span className="font-medium">{selectedPractice.practiceName}</span>
                <span className="text-muted-foreground">•</span>
                <span>{selectedPractice.practiceAddress}</span>
                <span className="text-muted-foreground">•</span>
                <span>{selectedPractice.practicePostcode}</span>
              </div>
            </div>
          )}

          {/* Job Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="title" className={cn(
                "text-sm font-medium",
                theme === 'light' ? 'text-gray-700' : 'text-gray-200'
              )}>
                <Briefcase className="h-4 w-4 inline mr-1" />
                Job Title <span className="text-red-500">*</span>
              </Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="e.g., Dental Hygienist - Part Time"
                className={cn(
                  error.title ? 'border-red-500' : '',
                  theme === 'light' ? 'light-mode-input-fix' : 'bg-[#1F1F2E] border-gray-700 text-white'
                )}
              />
              {/* FIXED: Check for error properly */}
              {error.title && (
                <p className="text-red-500 text-xs">{error.title}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label className={cn(
                "text-sm font-medium",
                theme === 'light' ? 'text-gray-700' : 'text-gray-200'
              )}>
                <Clock className="h-4 w-4 inline mr-1" />
                Employment Type <span className="text-red-500">*</span>
              </Label>
              <Select 
                value={formData.employmentType} 
                onValueChange={(value) => handleInputChange('employmentType', value)}
              >
                <SelectTrigger className={cn(
                  error.employmentType ? 'border-red-500' : '',
                  theme === 'light' 
                    ? 'light-mode-input-fix' 
                    : 'bg-[#1F1F2E] border-gray-700 text-white'
                )}>
                  <SelectValue placeholder="Select employment type" />
                </SelectTrigger>
                <SelectContent className={cn(
                  theme === 'light' ? 'bg-white text-gray-900' : 'bg-[#1F1F2E] text-white border-gray-700'
                )}>
                  {employmentTypes.map((type) => (
                    <SelectItem 
                      key={type} 
                      value={type}
                      className={cn(
                        theme === 'light' ? 'text-gray-900 hover:bg-gray-100' : 'text-white hover:bg-gray-700'
                      )}
                    >
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* FIXED: Check for error properly */}
              {error.employmentType && (
                <p className="text-red-500 text-xs">{error.employmentType}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="salary" className={cn(
              "text-sm font-medium",
              theme === 'light' ? 'text-gray-700' : 'text-gray-200'
            )}>
              Salary/Rate (Optional)
            </Label>
            <Input
              id="salary"
              value={formData.salary}
              onChange={(e) => handleInputChange('salary', e.target.value)}
              placeholder="e.g., £25-30/hour, £35,000-40,000/year, Competitive"
              className={cn(
                theme === 'light' ? 'light-mode-input-fix' : 'bg-[#1F1F2E] border-gray-700 text-white'
              )}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description" className={cn(
              "text-sm font-medium",
              theme === 'light' ? 'text-gray-700' : 'text-gray-200'
            )}>
              Job Description <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Describe the role, responsibilities, and what makes this position attractive..."
              className={cn(
                "min-h-[120px]",
                error.description ? 'border-red-500' : '',
                theme === 'light' 
                  ? 'bg-white border-gray-300 text-gray-900 placeholder:text-gray-500' 
                  : 'bg-[#1F1F2E] border-gray-700 text-white placeholder:text-gray-400'
              )}
            />
            {/* FIXED: Check for error properly */}
            {error.description && (
              <p className="text-red-500 text-xs">{error.description}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="requirements" className={cn(
              "text-sm font-medium",
              theme === 'light' ? 'text-gray-700' : 'text-gray-200'
            )}>
              Requirements & Qualifications (Optional)
            </Label>
            <Textarea
              id="requirements"
              value={formData.requirements}
              onChange={(e) => handleInputChange('requirements', e.target.value)}
              placeholder="List required qualifications, experience, certifications..."
              className={cn(
                "min-h-[100px]",
                theme === 'light' 
                  ? 'bg-white border-gray-300 text-gray-900 placeholder:text-gray-500' 
                  : 'bg-[#1F1F2E] border-gray-700 text-white placeholder:text-gray-400'
              )}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="applicationInstructions" className={cn(
              "text-sm font-medium",
              theme === 'light' ? 'text-gray-700' : 'text-gray-200'
            )}>
              <Users className="h-4 w-4 inline mr-1" />
              How to Apply <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="applicationInstructions"
              value={formData.applicationInstructions}
              onChange={(e) => handleInputChange('applicationInstructions', e.target.value)}
              placeholder="Instructions for candidates on how to apply (email CV, call, visit in person, etc.)"
              className={cn(
                "min-h-[80px]",
                error.applicationInstructions ? 'border-red-500' : '',
                theme === 'light' 
                  ? 'bg-white border-gray-300 text-gray-900 placeholder:text-gray-500' 
                  : 'bg-[#1F1F2E] border-gray-700 text-white placeholder:text-gray-400'
              )}
            />
            {/* FIXED: Check for error properly */}
            {error.applicationInstructions && (
              <p className="text-red-500 text-xs">{error.applicationInstructions}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="contactEmail" className={cn(
              "text-sm font-medium",
              theme === 'light' ? 'text-gray-700' : 'text-gray-200'
            )}>
              Contact Email <span className="text-red-500">*</span>
            </Label>
            <Input
              id="contactEmail"
              type="email"
              value={formData.contactEmail}
              onChange={(e) => handleInputChange('contactEmail', e.target.value)}
              placeholder="Email for candidates to apply"
              className={cn(
                error.contactEmail ? 'border-red-500' : '',
                theme === 'light' ? 'light-mode-input-fix' : 'bg-[#1F1F2E] border-gray-700 text-white'
              )}
            />
            {/* FIXED: Check for error properly */}
            {error.contactEmail && (
              <p className="text-red-500 text-xs">{error.contactEmail}</p>
            )}
          </div>

          {/* Job Expiration Date */}
          <div className="space-y-2">
            <Label 
              htmlFor="expiresAt" 
              className={cn(
                "text-sm font-medium",
                theme === 'light' ? 'text-gray-700' : 'text-white'
              )}
            >
              Job Expiration Date
            </Label>
            <input
              type="date"
              id="expiresAt"
              value={formData.expiresAt ? formData.expiresAt.toISOString().split('T')[0] : ''}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                expiresAt: e.target.value ? new Date(e.target.value) : null
              }))}
              style={{
                // Force inline styles to override any global CSS
                backgroundColor: theme === 'light' ? '#ffffff' : '#1F1F2E',
                borderColor: theme === 'light' ? '#d1d5db' : '#4b5563',
                color: theme === 'light' ? '#111827' : '#ffffff',
                // Calendar icon color fix
                colorScheme: theme === 'light' ? 'light' : 'dark',
              }}
              className={cn(
                "w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500",
                // Remove all hover classes and handle via inline styles
                "focus:border-purple-500"
              )}
              onMouseEnter={(e) => {
                // Force hover styles via JavaScript
                if (theme === 'light') {
                  e.target.style.backgroundColor = '#f9fafb';
                  e.target.style.borderColor = '#9ca3af';
                } else {
                  e.target.style.backgroundColor = '#2A2A3E';
                  e.target.style.borderColor = '#6b7280';
                }
              }}
              onMouseLeave={(e) => {
                // Reset to normal styles
                if (theme === 'light') {
                  e.target.style.backgroundColor = '#ffffff';
                  e.target.style.borderColor = '#d1d5db';
                } else {
                  e.target.style.backgroundColor = '#1F1F2E';
                  e.target.style.borderColor = '#4b5563';
                }
              }}
              required
            />
            <p className={cn(
              "text-xs",
              theme === 'light' ? 'text-gray-500' : 'text-gray-400'
            )}>
              Job listings will automatically become inactive after this date. Default is 30 days from posting.
            </p>
          </div>

          {/* Location Field - Add this after salary field */}
          <div className="space-y-2">
            <Label htmlFor="location" className={cn(
              "text-sm font-medium",
              theme === 'light' ? 'text-gray-700' : 'text-gray-200'
            )}>
              <MapPin className="h-4 w-4 inline mr-1" />
              Job Location
            </Label>
            <Input
              id="location"
              value={formData.location}
              onChange={(e) => handleInputChange('location', e.target.value)}
              placeholder="City, County (auto-filled from practice address)"
              className={cn(
                theme === 'light' ? 'light-mode-input-fix' : 'bg-[#1F1F2E] border-gray-700 text-white'
              )}
            />
            <p className={cn(
              "text-xs",
              theme === 'light' ? 'text-gray-500' : 'text-gray-400'
            )}>
              This will be displayed on job cards. Auto-filled from practice address but can be customized.
            </p>
          </div>

          {/* Form Actions */}
          <div className="flex gap-4 pt-4 border-t">
            <Button
              type="submit"
              disabled={loading || verifiedPractices.length === 0}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {loading ? 'Posting...' : (initialData ? 'Update Job' : 'Post Job')}
            </Button>
            
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={loading}
              className={cn(
                theme === 'light' ? 'text-gray-700 border-gray-300 hover:bg-gray-100' : 'text-gray-300 border-gray-600 hover:bg-gray-700'
              )}
            >
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default JobPostingForm;
